// Detailed, auto-injected 12-section build prompt (2,000–5,000 words) for any idea.
export const generateDetailedBuilderPrompt = (b, builder) => {
  if (!b) return "";
  const features = b.coreFeatures || [];
  const tags = b.tags || [];
  const slug = (s) => (s || "item").toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "");
  const lower = (s) => (s || "").toLowerCase();
  const stack = builder?.stack || "Modern React + Supabase";
  const builderName = builder?.name || "your AI builder";
  const name = b.title;

  const businessType = /marketplace/i.test(tags.join(" ")) || /marketplace/i.test(b.summary || "")
    ? "two-sided marketplace platform"
    : "SaaS web application";

  // ---- Section 3: feature descriptions ----
  const coreFeatureLines = features.map(
    (f) => `- ${f}: Deliver a complete, production-grade "${lower(f)}" module. Include all create, read, update, and delete actions where relevant, real-time state, validation, empty/loading/error states, and full persistence to the database. This capability is central to ${name} and must be flawless.`
  ).join("\n");

  const platformFeatures = [
    ["Google Authentication & Accounts", "Secure sign-up, sign-in, and session management via Google OAuth. Store profile, role, and plan. Protect every private route."],
    ["Personalized Dashboard", `The home base after login. Show real, live data relevant to ${name}: the user's items, activity, quick actions, stats, and recent updates. No placeholder widgets.`],
    ["Global Search & Filtering", "Fast, typo-tolerant search with filters, sorting, and pagination across the platform's core entities."],
    ["Notifications", "In-app notification center plus email and push notifications for key events (new messages, status changes, reminders)."],
    ["Messaging / Chat", "Real-time, threaded messaging between the relevant user types with read states and safety controls."],
    ["Favorites & Saved Items", "Let users save, organize, and revisit items they care about."],
    ["Payments & Checkout", "Stripe-powered checkout with secure payment intents, receipts, and webhook handling."],
    ["Subscriptions & Billing", "Plan selection, upgrades, downgrades, cancellations, invoices, and billing history."],
    ["Reviews & Ratings", "Verified reviews, star ratings, and moderation."],
    ["Reports & Analytics", "Dashboards that visualize real usage, revenue, and engagement metrics."],
    ["AI Assistant & Recommendations", `An embedded AI assistant tailored to ${name} that answers questions, recommends next actions, and automates repetitive work.`],
    ["User Profiles & Settings", "Editable profiles, preferences, privacy controls, and account management."],
    ["Admin Panel", "A complete back office to run the platform (see section 7)."],
    ["Public REST API", "Documented, authenticated API endpoints for every core resource."],
    ["Mobile Optimization", "Fully responsive, mobile-first layouts with touch-friendly interactions."],
  ].map(([t, d]) => `- ${t}: ${d}`).join("\n");

  // ---- Section 5: screens with purpose ----
  const screenDefs = [
    ["Landing Page", `Communicate what ${name} does, the problem it solves, social proof, and a clear primary call to action.`],
    ["Login / Register / Forgot Password", "Frictionless Google authentication with graceful error handling."],
    ["Onboarding", "Capture the essentials needed to personalize the experience, then route users to the dashboard."],
    ["Dashboard", "Real data, quick actions, and the user's most important information at a glance."],
    ["Search & Results", "Discover and filter the platform's core entities."],
    ...features.slice(0, 5).map((f) => [`${f} Screen`, `A dedicated, fully functional screen to use and manage "${lower(f)}".`]),
    ["Detail / Item Page", "Rich detail view with all relevant information and primary actions (save, message, purchase)."],
    ["Notifications", "A prioritized, filterable feed of everything relevant to the user."],
    ["Messages", "Threaded conversations with real-time updates."],
    ["Payments & Checkout", "Clear pricing, secure payment, and confirmation."],
    ["Subscriptions & Billing", "Manage plan, payment methods, and invoices."],
    ["Reviews", "Read and write verified reviews."],
    ["Settings & Profile", "Manage account, preferences, and privacy."],
    ["Help Center", "Guides, FAQs, and support contact."],
    ["Admin Dashboard & Analytics", "Operational overview with real platform metrics."],
  ].map(([t, d]) => `- ${t}: ${d}`).join("\n");

  // ---- Section 6: database ----
  const dbTables = [
    "users (id, name, email, avatar, role, plan, created_at)",
    "sessions (id, user_id, token, expires_at)",
    "businesses (id, owner_id, name, description, status, created_at)",
    ...features.slice(0, 5).map((f) => `${slug(f)} (id, user_id, data, status, created_at)`),
    "subscriptions (id, user_id, plan, status, renews_at)",
    "payments (id, user_id, amount, currency, status, created_at)",
    "reviews (id, user_id, target_id, rating, comment, created_at)",
    "messages (id, thread_id, sender_id, body, created_at)",
    "notifications (id, user_id, type, payload, read, created_at)",
    "analytics_events (id, user_id, event, metadata, created_at)",
    "activity_logs (id, actor_id, action, target, created_at)",
  ].map((t) => `- ${t}`).join("\n");

  const aiFeatures = [
    `Smart recommendations that surface the most relevant items and next steps for each ${name} user.`,
    "AI-enhanced search that understands natural-language queries.",
    "Predictive insights and trend detection from platform activity.",
    "Automation of repetitive workflows to save users time.",
    "Auto-generated summaries and digests.",
    "Smart notifications that fire at the right moment.",
    `An in-app AI assistant that guides users through ${name}.`,
  ].map((x) => `- ${x}`).join("\n");

  return `🚀 BUILD PROMPT

You are an expert Senior Product Manager, UX Designer, Solution Architect, and Full Stack Software Engineer with a decade of experience shipping successful commercial software.

Your task is to build a complete, production-ready MVP called:

"${name}"

Do NOT create a prototype, mockup, or demo. Build a real, scalable ${businessType} that is modern, clean, mobile-first, responsive, secure, and production-ready. The application should feel comparable to Stripe, Airbnb, Notion, Linear, or Apple in design quality and polish. Everything must be connected and fully functional end to end — every button performs an action, every form writes to the database, and every dashboard displays real data.

====================================================
1. BUSINESS OVERVIEW
====================================================
Business Name: ${name}
Business Category: ${b.category_name || b.category}
Business Type: ${businessType}
Industry: ${b.category_name || b.category}
Revenue Model: ${b.revenueModel || "Subscription / SaaS"}
Target Users: The core audience for ${name} — people and businesses within ${b.category_name || "this space"}${tags.length ? `, especially those interested in ${tags.join(", ")}` : ""}.
Primary Goal: ${b.solution || "Solve the core problem end to end with a delightful, reliable product."}
Problem Being Solved: ${b.problem || ""}
Why This Business Matters: ${b.summary || ""} This is a real, monetizable opportunity, and the product should be built to launch commercially.
Estimated Build Time: ${b.buildTime || "4–8 weeks"}. Complexity: ${b.difficulty || "Medium"}.

Interpret the vision above and make sensible, founder-minded product decisions wherever detail is not specified. Optimize for clarity, trust, speed, and conversion.

====================================================
2. USER TYPES & PERMISSIONS
====================================================
Implement strict role-based access control. Every route, action, and API endpoint must check the user's role.

- Guest: Browse public pages, search, view details, and sign up. Cannot save data, transact, or message.
- Registered User: Full access to core features, can save items, message, transact, manage a profile, and receive notifications.
- Premium User: Everything a registered user can do, plus advanced features, higher limits, priority support, and premium-only tools.
- Business: Create and manage listings/offers/services, respond to customers, access a business dashboard with analytics, and manage billing.
- Administrator: Complete platform control — manage users, businesses, content, categories, payments, subscriptions, approvals, support, and platform settings.

====================================================
3. COMPLETE FEATURE LIST
====================================================
Core features specific to ${name}:
${coreFeatureLines || "- Define the core modules that directly deliver the product's primary value."}

Platform features every user expects:
${platformFeatures}

Do not skip any feature. Each must be fully implemented, connected to the database, and reflected across the relevant dashboards and screens.

====================================================
4. USER FLOW (everything connected)
====================================================
Landing Page → Google Login → Onboarding → Dashboard → Search / Discover → ${features[0] || "Core Feature"} → Detail Page → Action / Purchase → Confirmation → Profile & Dashboard.

Each step must persist data and update the rest of the app in real time. A returning user lands directly on their personalized dashboard. Every transition is smooth, every dead-end avoided, and every screen reachable through clear navigation.

====================================================
5. EVERY SCREEN (generate all, no placeholders)
====================================================
${screenDefs}

====================================================
6. DATABASE (complete schema & relationships)
====================================================
${dbTables}

Define primary keys, foreign keys, indexes, and relationships. Users relate to their items, businesses, payments, subscriptions, messages, reviews, and notifications. Enforce referential integrity and cascade rules where appropriate. Seed realistic sample data so every screen renders real content.

====================================================
7. ADMIN PANEL
====================================================
Build a full back office that lets an administrator operate ${name}:
- Manage Users (view, edit roles, suspend, delete)
- Manage Businesses / listings (approve, edit, remove)
- Reports (usage, content, abuse)
- Analytics (growth, engagement, retention)
- Subscriptions (plans, status, churn)
- Revenue (transactions, payouts, refunds)
- Support Tickets (respond, resolve)
- Categories (create, edit, organize)
- Approvals (moderation queue)
- Platform Settings (fees, feature flags, branding)

====================================================
8. AI FEATURES
====================================================
${aiFeatures}

====================================================
9. MONETIZATION
====================================================
Primary revenue model: ${b.revenueModel || "Subscription"}. Implement:
- Free Plan: core access with sensible usage limits to drive activation.
- Premium: advanced features, higher limits, and priority tools for individuals.
- Business: multi-user seats, analytics, and priority support.
- Enterprise: custom pricing, SSO, SLAs, and dedicated support.
- One-time purchases and upsells where they fit the product.
- Commission or transaction fees for any marketplace or payment flows.
- Optional advertising or sponsored placements where relevant.
Wire all paid flows to Stripe with clean upgrade/downgrade and billing management.

====================================================
10. TECHNOLOGY
====================================================
- Modern React${builder?.name === "Emergent" ? " + FastAPI + MongoDB" : ""} (${stack}); use Next.js where suitable.
- TailwindCSS for styling with reusable, composable components.
- Supabase (or equivalent) for database, storage, and realtime.
- Google Authentication for sign-in.
- Stripe for payments and subscriptions.
- Responsive, SEO-optimized, and performance-optimized (fast loads, lazy loading, caching).
- Secure by default (input validation, authz checks, rate limiting, secrets in env).
- Scalable architecture with clean, maintainable, well-structured code.

====================================================
11. UI STYLE
====================================================
Minimal, premium, Apple-inspired. White backgrounds, black typography, soft shadows, rounded cards, generous spacing, professional typography, tasteful gold accents, beautiful micro-animations, and excellent UX. Fast, clean, and unmistakably modern SaaS.

====================================================
12. DELIVERABLES
====================================================
- Complete UI and UX for every screen
- Complete database with relationships and seed data
- Authentication and role-based authorization
- Public, documented REST API
- Full admin dashboard
- Fully responsive design
- Production-ready, deployment-ready code
- No placeholder components or dead buttons
- A fully functional MVP that could launch commercially

====================================================
13. NON-FUNCTIONAL REQUIREMENTS
====================================================
- Security: validate and sanitize all input, enforce authorization on every endpoint, hash and protect secrets in environment variables, guard against common web vulnerabilities (XSS, CSRF, injection), and rate-limit sensitive actions.
- Performance: fast initial load, code-splitting and lazy loading, optimized images, cached queries, and pagination for large lists. Target sub-second interactions.
- Reliability: graceful error handling everywhere, retries for transient failures, and clear user-facing error and empty states.
- Accessibility: semantic HTML, keyboard navigation, sufficient color contrast, focus states, and screen-reader labels.
- Observability: structured logging, activity logs, and analytics events for key actions.
- Scalability: stateless services, indexed queries, and a schema that supports growth to thousands of users without rework.

====================================================
14. ACCEPTANCE CRITERIA & DEFINITION OF DONE
====================================================
- Every screen listed above exists, is reachable through navigation, and renders real data (no placeholders or lorem ipsum).
- Every button, link, and form is wired to a real action and persists to the database.
- Authentication works end to end with role-based access enforced across the app.
- Payments and subscriptions complete successfully through Stripe with receipts and webhook handling.
- The admin panel can fully operate the platform (users, content, revenue, settings).
- The app is fully responsive on mobile, tablet, and desktop, and passes a visual QA pass.
- Seed data is present so a first-time reviewer sees a populated, believable product.
- Code is clean, componentized, documented where non-obvious, and deployment-ready.

====================================================
END PROMPT
====================================================
Think like a startup founder building the next successful SaaS company. Do not skip features. Do not generate placeholder pages. Every screen should be functional. Every button should perform an action. Every form should connect to the database. Every dashboard should display real data. Write clean, maintainable, scalable production-ready code using reusable components and best practices. The finished application should be something that could realistically launch as a commercial software product.

Now build "${name}" with ${builderName}. Start by scaffolding authentication and the database, then implement the dashboard, then each core feature in order, and finish with payments, the admin panel, and a responsive QA pass.`;
};
